import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { api } from "../../../api/apiService";
import { Link } from "react-router-dom";

const schema = yup.object().shape({
  role_name: yup.string().required("Role name is required"),
});

const Role = () => {
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (currentRole) {
      reset({
        role_name: currentRole.role_name || "",
        description: currentRole.description || "",
      });
    }
  }, [currentRole, reset]);

  const fetchRoles = async () => {
    try {
      const response = await api.get("/roles");
      setRoles(response.data.data);
    } catch (error) {
      console.error("Error fetching roles: ", error);
      toast.error("Failed to fetch roles");
    }
  };

  const handleAddRole = () => {
    setCurrentRole({
      role_name: "",
      description: "",
    });
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEditRole = (role) => {
    setCurrentRole(role);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDeleteRole = async (roleId) => {
    if (window.confirm("Are you sure you want to delete this role?")) {
      try {
        await api.delete(`/roles/${roleId}`);
        setRoles((prev) => prev.filter((role) => role.role_id !== roleId));
        toast.success("Role deleted successfully");
      } catch (error) {
        console.error("Error deleting role: ", error);
        toast.error("Error deleting role");
      }
    }
  };

  const saveRole = async (data) => {
    setIsLoading(true);
    try {
      if (isEditing && currentRole) {
        const roleId = currentRole.role_id;

        if (!roleId) {
          throw new Error("Role ID is undefined");
        }

        await api.put(`/roles/${roleId}`, data);
        toast.success("Role updated successfully");
      } else {
        await api.post("/roles", data);
        toast.success("Role added successfully");
      }
      fetchRoles();
      setShowModal(false);
      reset();
    } catch (error) {
      if (error.data && error.data.errors) {
        error.data.errors.forEach(err => {
          toast.error(err);
        });
      } else {
        toast.error("Error saving role");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main id="main" className="main">
      <div className="pagetitle">
        <h1>Roles</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/home">Home</Link>
            </li>
            <li className="breadcrumb-item active">Roles</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Role Management</h5>
                <div>
                  <button className="btn btn-primary mb-3" onClick={handleAddRole}>
                    Add Role
                  </button>
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roles.map((role, index) => (
                        <tr key={role.role_id || index}>
                          <td>{role.role_name}</td>
                          <td>{role.description}</td>
                          <td>
                            <button
                              className="btn btn-warning btn-sm me-2"
                              onClick={() => handleEditRole(role)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDeleteRole(role.role_id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {showModal && (
                    <div className="modal fade show d-block">
                      <div className="modal-dialog">
                        <div className="modal-content">
                          <div className="modal-header">
                            <h5 className="modal-title">
                              {isEditing ? "Edit Role" : "Add Role"}
                            </h5>
                            <button
                              type="button"
                              className="btn-close"
                              onClick={() => setShowModal(false)}
                            ></button>
                          </div>
                          <div className="modal-body">
                            <form onSubmit={handleSubmit(saveRole)}>
                              <div className="mb-3">
                                <label className="form-label">Role Name</label>
                                <input
                                  type="text"
                                  className={`form-control ${errors.role_name ? "is-invalid" : ""}`}
                                  {...register("role_name")}
                                />
                                {errors.role_name && (
                                  <div className="invalid-feedback">{errors.role_name.message}</div>
                                )}
                              </div>
                              <div className="mb-3">
                                <label className="form-label">Description</label>
                                <textarea
                                  className={`form-control ${errors.description ? "is-invalid" : ""}`}
                                  {...register("description")}
                                ></textarea>
                                {errors.description && (
                                  <div className="invalid-feedback">{errors.description.message}</div>
                                )}
                              </div>
                              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                {isLoading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {isEditing ? "Updating..." : "Saving..."}
                                  </>
                                ) : (
                                  <>{isEditing ? "Update Role" : "Save Role"}</>
                                )}
                              </button>
                            </form>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Role;